export default class IdleTimeoutService {
	constructor(options = {}) {
		this.idleTime = options.idleTime || 30 * 60 * 1000; // 30 minutes default
		this.countdownTime = options.countdownTime || 30; // 30 seconds countdown
		this.warningTimer = null;
		this.countdownTimer = null;
		this.lastActivity = Date.now();
		this.events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
		this.onTimeout = options.onTimeout || (() => {});
		this.onShowModal = options.onShowModal || (() => {});
		this.onHideModal = options.onHideModal || (() => {});
		this.onCountdown = options.onCountdown || (() => {});
		this.isPromptOpen = false;
		this.storageKey = 'idle_timeout_state';
		this.broadcastChannel = null;
		this.countdownStartTime = null;
		this.isLocalTab = true; // Track if this tab initiated the countdown
		
		// Initialize BroadcastChannel for cross-tab communication
		if (typeof BroadcastChannel !== 'undefined') {
			this.broadcastChannel = new BroadcastChannel('idle_timeout_channel');
			this.broadcastChannel.onmessage = (event) => {
				this.handleBroadcastMessage(event.data);
			};
		}
		
		// Listen to storage events for cross-tab synchronization
		window.addEventListener('storage', (e) => {
			if (e.key === this.storageKey) {
				this.handleStorageChange(e.newValue);
			}
		});
	}
	
	start() {
		// Reset timer on user activity
		this.events.forEach(event => {
			document.addEventListener(event, () => this.resetTimer());
		});
		
		// Check if another tab already started countdown
		this.checkExistingState();
		
		this.resetTimer();
	}
	
	checkExistingState() {
		try {
			const stored = localStorage.getItem(this.storageKey);
			if (stored) {
				const state = JSON.parse(stored);
				const now = Date.now();
				
				// Check if countdown is active
				if (state.countdownStartTime) {
					const elapsed = Math.floor((now - state.countdownStartTime) / 1000);
					const remaining = this.countdownTime - elapsed;
					
					if (remaining > 0 && remaining <= this.countdownTime) {
						// Another tab started countdown, sync this tab
						this.isLocalTab = false;
						this.isPromptOpen = true;
						this.countdownStartTime = state.countdownStartTime;
						this.onShowModal(remaining);
						this.startCountdown();
					} else if (remaining <= 0) {
						// Countdown expired, clear state and let normal flow continue
						this.clearStoredState();
					}
				}
			}
		} catch (e) {
			console.error('Error checking existing state:', e);
		}
	}
	
	handleStorageChange(newValue) {
		if (!newValue) {
			// State cleared, hide modal if open
			if (this.isPromptOpen) {
				this.hideWarningPrompt();
			}
			return;
		}
		
		try {
			const state = JSON.parse(newValue);
			const now = Date.now();
			
			if (state.countdownStartTime) {
				const elapsed = Math.floor((now - state.countdownStartTime) / 1000);
				const remaining = this.countdownTime - elapsed;
				
				if (remaining > 0 && !this.isPromptOpen) {
					// Another tab started countdown, sync this tab
					this.isLocalTab = false;
					this.isPromptOpen = true;
					this.countdownStartTime = state.countdownStartTime;
					this.onShowModal(remaining);
					this.startCountdown();
				}
			}
		} catch (e) {
			console.error('Error handling storage change:', e);
		}
	}
	
	handleBroadcastMessage(data) {
		if (data.type === 'countdown_update') {
			// Update countdown from another tab
			if (this.isPromptOpen && !this.isLocalTab) {
				this.onCountdown(data.secondsLeft);
			}
		} else if (data.type === 'countdown_start') {
			// Another tab started countdown
			if (!this.isPromptOpen) {
				this.isLocalTab = false;
				this.isPromptOpen = true;
				this.countdownStartTime = data.countdownStartTime;
				this.onShowModal(data.secondsLeft);
				this.startCountdown();
			}
		} else if (data.type === 'countdown_stop') {
			// Another tab stopped countdown
			if (this.isPromptOpen && !this.isLocalTab) {
				this.hideWarningPrompt();
			}
		} else if (data.type === 'logout') {
			// Another tab triggered logout
			this.triggerLogout();
		}
	}
	
	resetTimer() {
		if (this.isPromptOpen) return;
		
		this.lastActivity = Date.now();
		clearTimeout(this.warningTimer);
		
		// Clear any stored countdown state only if we're resetting (not if another tab is counting down)
		const stored = localStorage.getItem(this.storageKey);
		if (!stored || !JSON.parse(stored).countdownStartTime) {
			this.clearStoredState();
		}
		
		this.warningTimer = setTimeout(() => {
			this.showWarningPrompt();
		}, this.idleTime);
	}
	
	showWarningPrompt() {
		this.isPromptOpen = true;
		this.isLocalTab = true;
		this.countdownStartTime = Date.now();
		let secondsLeft = this.countdownTime;
		
		// Store state in localStorage for cross-tab sync
		this.storeState({ countdownStartTime: this.countdownStartTime });
		
		// Broadcast to other tabs
		if (this.broadcastChannel) {
			this.broadcastChannel.postMessage({
				type: 'countdown_start',
				countdownStartTime: this.countdownStartTime,
				secondsLeft: secondsLeft
			});
		}
		
		// Call callbacks to show modal
		if (this.onShowModal) {
			this.onShowModal(secondsLeft);
		}
		if (this.onCountdown) {
			this.onCountdown(secondsLeft);
		}
		this.startCountdown();
	}
	
	startCountdown() {
		clearInterval(this.countdownTimer);
		
		this.countdownTimer = setInterval(() => {
			if (!this.countdownStartTime) {
				clearInterval(this.countdownTimer);
				return;
			}
			
			const now = Date.now();
			const elapsed = Math.floor((now - this.countdownStartTime) / 1000);
			const secondsLeft = this.countdownTime - elapsed;
			
			if (secondsLeft <= 0) {
				clearInterval(this.countdownTimer);
				this.hideWarningPrompt();
				this.triggerLogout();
			} else {
				this.onCountdown(secondsLeft);
				
				// Broadcast countdown update to other tabs
				if (this.broadcastChannel && this.isLocalTab) {
					this.broadcastChannel.postMessage({
						type: 'countdown_update',
						secondsLeft: secondsLeft
					});
				}
			}
		}, 100); // Check every 100ms for accuracy, especially when tab is not active
	}
	
	userChoseToStayLoggedIn() {
		this.hideWarningPrompt();
		this.resetTimer();
	}
	
	userChoseToLogout() {
		this.hideWarningPrompt();
		this.triggerLogout();
	}
	
	triggerLogout() {
		// Broadcast logout to all tabs
		if (this.broadcastChannel) {
			this.broadcastChannel.postMessage({ type: 'logout' });
		}
		
		// Clear stored state
		this.clearStoredState();
		
		// Trigger logout callback
		this.onTimeout();
	}
	
	hideWarningPrompt() {
		clearInterval(this.countdownTimer);
		this.isPromptOpen = false;
		this.countdownStartTime = null;
		this.isLocalTab = true;
		
		// Clear stored state
		this.clearStoredState();
		
		// Broadcast to other tabs
		if (this.broadcastChannel) {
			this.broadcastChannel.postMessage({ type: 'countdown_stop' });
		}
		
		this.onHideModal();
	}
	
	storeState(state) {
		try {
			localStorage.setItem(this.storageKey, JSON.stringify({
				...state,
				timestamp: Date.now()
			}));
		} catch (e) {
			console.error('Error storing state:', e);
		}
	}
	
	clearStoredState() {
		try {
			localStorage.removeItem(this.storageKey);
		} catch (e) {
			console.error('Error clearing state:', e);
		}
	}
	
	stop() {
		clearTimeout(this.warningTimer);
		clearInterval(this.countdownTimer);
		this.events.forEach(event => {
			document.removeEventListener(event, () => this.resetTimer());
		});
		
		if (this.broadcastChannel) {
			this.broadcastChannel.close();
		}
		
		this.clearStoredState();
	}
}
